import React, { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { brandsOpenApi, COLORS, ROUTE_PATHS } from '@constants';
import { FEED_SUPPORTED_PLATFORMS } from '@enums';
import { useStore } from '@hooks';
import { Icon, Typography } from '@ui';
import { axiosBoa } from '@utils';
import { Flex, notification } from 'antd';
import { observer } from 'mobx-react';
import { usePreWizardContext } from '../../PreWizardContext';
import { Container, OptionCard, OptionContent, OptionDescription, OptionRow, OptionsGrid, OptionTitle } from './styled';

const Step0ManualFeed = observer(() => {
    const navigate = useNavigate();
    const store = useStore();
    const { isManualStoreWithNoCampaigns } = store.accountsStore;
    const { currentAccountId } = store.authStore;
    const { selectedOption, setSelectedOption, setIsButtonEnabled, setOnButtonClick } = usePreWizardContext();

    const handleOptionSelect = (option) => {
        setSelectedOption(option);
    };

    const handleSyncCatalog = async () => {
        if (!selectedOption) return;

        if (selectedOption === FEED_SUPPORTED_PLATFORMS.GOOGLE || selectedOption === FEED_SUPPORTED_PLATFORMS.META) {
            try {
                const { url } = await axiosBoa.get(brandsOpenApi.feedOauth.redirect(selectedOption, currentAccountId));
                window.location.href = url;
            } catch (error) {
                notification.error({
                    message: 'Something went wrong'
                });
            }
            return;
        }

        if (selectedOption === 'csv') {
            navigate(ROUTE_PATHS.CAMPAIGNS_CREATE_MANUAL_CSV);
        }
    };

    useEffect(() => {
        setIsButtonEnabled(!!selectedOption);
    }, [selectedOption, setIsButtonEnabled]);

    useEffect(() => {
        setOnButtonClick(() => handleSyncCatalog);
    }, [setOnButtonClick, selectedOption, currentAccountId]);

    if (!isManualStoreWithNoCampaigns) {
        return <Navigate to={ROUTE_PATHS.CAMPAIGNS_CREATE_STEP_1} replace />;
    }

    return (
        <Flex vertical style={{ flexGrow: 1 }}>
            <Container>
                <Typography.Title
                    level={1}
                    color={COLORS.$gray100Black}
                    style={{ marginBottom: '16px' }}
                    data-e2e='onboarding-wizard_connect-via-product-feed_title'
                >
                    Connection via product feed
                </Typography.Title>

                <Typography.Text
                    style={{ marginBottom: '24px', display: 'block' }}
                    color={COLORS.$gray60Black}
                    type='body2'
                    data-e2e='onboarding-wizard_connect-via-product-feed_description'
                >
                    To efficiently run ads on Klickly, please provide access to your product catalog. You can upload it
                    using one of the methods listed below:
                </Typography.Text>

                <OptionsGrid>
                    <OptionRow>
                        <OptionCard
                            $isSelected={selectedOption === FEED_SUPPORTED_PLATFORMS.GOOGLE}
                            onClick={() => handleOptionSelect(FEED_SUPPORTED_PLATFORMS.GOOGLE)}
                            data-e2e='onboarding-wizard_connect-via-google_card'
                        >
                            <Icon
                                size='80px'
                                style={{ fontSize: '80px' }}
                                component={() => <Icon.GoogleIcon style={{ width: '80px', height: '80px' }} />}
                            />
                            <OptionContent>
                                <OptionTitle>Connect with Google Product Feed</OptionTitle>
                                <OptionDescription>
                                    Import your catalog directly from Google Merchant Center
                                </OptionDescription>
                            </OptionContent>
                        </OptionCard>

                        <OptionCard
                            $isSelected={selectedOption === FEED_SUPPORTED_PLATFORMS.META}
                            onClick={() => handleOptionSelect(FEED_SUPPORTED_PLATFORMS.META)}
                            data-e2e='onboarding-wizard_connect-via-meta_card'
                        >
                            <Icon
                                size='80px'
                                style={{ fontSize: '80px' }}
                                component={() => <Icon.MetaIcon style={{ width: '80px', height: '80px' }} />}
                            />
                            <OptionContent>
                                <OptionTitle>Connect with Meta Commerce Catalog</OptionTitle>
                                <OptionDescription>Sync your catalog from Meta Business Suite</OptionDescription>
                            </OptionContent>
                        </OptionCard>
                    </OptionRow>

                    <OptionRow>
                        <OptionCard
                            $isSelected={selectedOption === 'csv'}
                            onClick={() => handleOptionSelect('csv')}
                            data-e2e='onboarding-wizard_import-via-csv_card'
                        >
                            <Icon
                                color={COLORS.$gray60Black}
                                size='80px'
                                style={{ fontSize: '80px' }}
                                strokeWidth={2}
                                component={() => <Icon.KlicklyLogo style={{ width: '80px', height: '80px' }} />}
                            />
                            <OptionContent>
                                <OptionTitle>Import products via CSV</OptionTitle>
                                <OptionDescription>
                                    Manually upload your catalog using Klickly&apos;s CSV template. You&apos;ll need to
                                    re-upload a refreshed CSV periodically
                                </OptionDescription>
                            </OptionContent>
                        </OptionCard>
                    </OptionRow>
                </OptionsGrid>
            </Container>
        </Flex>
    );
});

export default Step0ManualFeed;
